// ============================================
// LEVANTAMIENTO.JS — Asistente guiado de levantamiento comercial
// 5 pasos: Origen → Necesidad → Técnico → Sitio → Resumen
// ============================================

import React, { useState, useEffect } from 'react';
import { BarraBusqueda, FiltrosRapidos, ContadorResultados } from './utils/filtros';
import { BadgeFolio, generarFolio } from './utils/folios';

const PP = "'Poppins', sans-serif";
const colores = { principal: '#5c9adb', oscuro: '#2f5f9e', gris: '#4e5357', grisClaro: '#5e666e', amarillo: '#dcde74', verde: '#5cd4b0', rojo: '#e05c5c', fondo: '#f3f4f4' };

// ============================================
// DATOS DE EJEMPLO
// ============================================
const levantamientosEjemplo = [
  {
    id: 1, folio: 'LEV-2026-001', estatus: 'Completado', fecha: '2026-03-25', horaRegistro: '10:30', ultimaMod: '2026-03-25 14:20', ultimoModUsuario: 'Carlos V.', comercial: 'Carlos V.',
    origen: { tipo: 'Llamada entrante', esExistente: true, clienteNombre: 'Bodegas Alianza', sucursal: 'Bodega Norte MTY' },
    necesidad: { servicios: ['CCTV'], descripcion: 'Necesita ampliar sistema de videovigilancia en zona de carga.', urgencia: 'Esta semana', presupuesto: '$50k-$150k' },
    tecnico: { cctv: { camaras: '8', instalacion: 'Exterior', cableado: 'Sí', dvr: 'Sí', marcaDvr: 'Hikvision 16CH', resolucion: '4K', remoto: 'Sí', zonasDificiles: 'Zona de carga tiene techos altos de 8m' } },
    sitio: { direccion: 'Av. Industrial 2500, Monterrey NL', tipo: 'Bodega', pisos: '1', m2: '3000', visita: 'Ya se hizo', observaciones: 'Bodega con techos de 8m, necesita cámaras tipo bullet con IR de largo alcance.' },
    score: 5,
  },
  {
    id: 2, folio: 'LEV-2026-002', estatus: 'Borrador', fecha: '2026-03-28', horaRegistro: '09:15', ultimaMod: '2026-03-29 11:00', ultimoModUsuario: 'Carlos V.', comercial: 'Carlos V.',
    origen: { tipo: 'WhatsApp', esExistente: false, nombre: 'Ing. Marcos Ruiz', empresa: 'Constructora del Valle', telefono: '55 4444 5555', correo: 'marcos@constructoravalle.com' },
    necesidad: { servicios: ['CCTV', 'Control de acceso'], descripcion: 'Edificio nuevo de oficinas, necesita sistema completo.', urgencia: 'Este mes', presupuesto: '$150k-$500k' },
    tecnico: {},
    sitio: {},
    score: 3,
  },
  {
    id: 3, folio: 'LEV-2026-003', estatus: 'Convertido', fecha: '2026-03-20', horaRegistro: '16:45', ultimaMod: '2026-03-22 09:30', ultimoModUsuario: 'Laura S.', comercial: 'Laura S.',
    origen: { tipo: 'Referido', esExistente: false, nombre: 'Patricia Ruiz', empresa: 'Colegio Montessori', telefono: '55 3333 4444', correo: 'patricia@montessori.edu.mx' },
    necesidad: { servicios: ['CCTV'], descripcion: '24 cámaras para instalación escolar completa con monitoreo remoto para padres.', urgencia: 'Esta semana', presupuesto: '$50k-$150k' },
    tecnico: { cctv: { camaras: '24', instalacion: 'Ambas', cableado: 'No', dvr: 'No', marcaDvr: '', resolucion: 'Full HD', remoto: 'Sí', zonasDificiles: 'Patio exterior sin techo — cámaras con protección IP67' } },
    sitio: { direccion: 'Av. Universidad 1200, CDMX', tipo: 'Otro', pisos: '2', m2: '1500', visita: 'Ya se hizo', observaciones: 'Colegio con áreas verdes amplias, estacionamiento y 3 accesos peatonales.' },
    score: 5,
  },
];

const serviciosDisponibles = [
  { id: 'CCTV', label: 'CCTV / Videovigilancia', icon: '📹' },
  { id: 'acceso', label: 'Control de acceso', icon: '🔐' },
  { id: 'redes', label: 'Redes e infraestructura', icon: '🌐' },
  { id: 'alarmas', label: 'Alarmas y seguridad', icon: '🔔' },
  { id: 'mantenimiento', label: 'Mantenimiento', icon: '🔧' },
  { id: 'automatizacion', label: 'Automatización', icon: '📱' },
  { id: 'otro', label: 'Otro', icon: '🏢' },
];

const pasos = [
  { id: 1, label: 'Origen', icon: '📞' },
  { id: 2, label: 'Necesidad', icon: '🎯' },
  { id: 3, label: 'Técnico', icon: '🔧' },
  { id: 4, label: 'Sitio', icon: '📍' },
  { id: 5, label: 'Resumen', icon: '✅' },
];

const inputStyle = { width: '100%', padding: '12px 14px', border: '1px solid #d0d0d0', borderRadius: '10px', fontSize: '16px', boxSizing: 'border-box', outline: 'none', color: '#4e5357', fontFamily: PP, background: '#fff', WebkitAppearance: 'none' };
const labelStyle = { fontSize: '12px', color: colores.grisClaro, display: 'block', marginBottom: '5px', fontWeight: '500', fontFamily: PP };
const tarjeta = { background: '#fff', borderRadius: '14px', padding: '20px', marginBottom: '14px' };

function calcScore(d) {
  let s = 0;
  if (d.necesidad?.presupuesto && d.necesidad.presupuesto !== 'No definido') s++;
  if (d.necesidad?.urgencia && d.necesidad.urgencia !== 'Sin definir') s++;
  if (d.sitio?.direccion) s++;
  if (d.origen?.esExistente || d.origen?.nombre) s++;
  if (d.necesidad?.servicios?.length > 0) s++;
  return s;
}

function ScoreEstrellas({ score }) {
  return (
    <div style={{ display: 'flex', gap: '4px' }}>
      {[1, 2, 3, 4, 5].map(i => (
        <span key={i} style={{ fontSize: '20px', color: i <= score ? '#f4a261' : '#d0d0d0' }}>{i <= score ? '★' : '☆'}</span>
      ))}
    </div>
  );
}

function EstatusBadge({ estatus }) {
  const cfgs = {
    'Borrador': { bg: '#fffbe6', color: '#7a6e00' },
    'Completado': { bg: '#e6f1fb', color: colores.oscuro },
    'Convertido': { bg: '#e8faf5', color: '#1D9E75' },
  };
  const c = cfgs[estatus] || { bg: '#f3f4f4', color: colores.grisClaro };
  return <span style={{ padding: '3px 12px', borderRadius: '10px', fontSize: '11px', fontWeight: '600', background: c.bg, color: c.color, fontFamily: PP }}>{estatus}</span>;
}

// ============================================
// COMPONENTE PRINCIPAL
// ============================================
function Levantamiento() {
  const [levantamientos, setLevantamientos] = useState(levantamientosEjemplo);
  const [vista, setVista] = useState('lista'); // lista | nuevo | detalle
  const [detalleId, setDetalleId] = useState(null);
  const [paso, setPaso] = useState(1);
  const [busqueda, setBusqueda] = useState('');
  const [filtroEstatus, setFiltroEstatus] = useState('');
  const [filtroFecha, setFiltroFecha] = useState('');

  // Form data — one object per step
  const [origen, setOrigen] = useState({ tipo: 'Llamada entrante', esExistente: false, clienteNombre: '', sucursal: '', nombre: '', empresa: '', telefono: '', correo: '' });
  const [necesidad, setNecesidad] = useState({ servicios: [], descripcion: '', urgencia: 'Sin definir', presupuesto: 'No definido' });
  const [tecnico, setTecnico] = useState({
    cctv: { camaras: '', instalacion: '', cableado: '', dvr: '', marcaDvr: '', resolucion: '', remoto: '', zonasDificiles: '' },
    acceso: { puntos: '', tipo: '', integracionCctv: '', horarios: '', registro: '' },
    redes: { puntos: '', area: '', rack: '', wifi: '', accessPoints: '', isp: '' },
    mantenimiento: { sistemas: '', instalador: '', contrato: '', frecuencia: '', problema: '' },
    alarmas: { zonas: '', tipo: '', monitoreo: '', lineaTel: '' },
  });
  const [sitio, setSitio] = useState({ direccion: '', tipo: 'Oficina', pisos: '', m2: '', visita: 'No', fechaVisita: '', observaciones: '' });

  const [width] = useState(window.innerWidth);
  const esCelular = width < 768;

  // Auto-save to localStorage
  useEffect(() => {
    if (vista === 'nuevo') {
      localStorage.setItem('beep-lev-draft', JSON.stringify({ origen, necesidad, tecnico, sitio, paso }));
    }
  }, [origen, necesidad, tecnico, sitio, paso, vista]);

  const resetForm = () => {
    setOrigen({ tipo: 'Llamada entrante', esExistente: false, clienteNombre: '', sucursal: '', nombre: '', empresa: '', telefono: '', correo: '' });
    setNecesidad({ servicios: [], descripcion: '', urgencia: 'Sin definir', presupuesto: 'No definido' });
    setTecnico({ cctv: { camaras: '', instalacion: '', cableado: '', dvr: '', marcaDvr: '', resolucion: '', remoto: '', zonasDificiles: '' }, acceso: { puntos: '', tipo: '', integracionCctv: '', horarios: '', registro: '' }, redes: { puntos: '', area: '', rack: '', wifi: '', accessPoints: '', isp: '' }, mantenimiento: { sistemas: '', instalador: '', contrato: '', frecuencia: '', problema: '' }, alarmas: { zonas: '', tipo: '', monitoreo: '', lineaTel: '' } });
    setSitio({ direccion: '', tipo: 'Oficina', pisos: '', m2: '', visita: 'No', fechaVisita: '', observaciones: '' });
    setPaso(1);
  };

  const iniciarNuevo = () => {
    const draft = localStorage.getItem('beep-lev-draft');
    if (draft) {
      try {
        const d = JSON.parse(draft);
        setOrigen(d.origen || origen);
        setNecesidad(d.necesidad || necesidad);
        setTecnico(d.tecnico || tecnico);
        setSitio(d.sitio || sitio);
        setPaso(d.paso || 1);
      } catch (e) { resetForm(); }
    } else { resetForm(); }
    setVista('nuevo');
  };

  const guardarLevantamiento = (estatus) => {
    const data = { origen, necesidad, tecnico, sitio };
    const score = calcScore(data);
    const ahora = new Date();
    const nuevo = {
      id: Date.now(), folio: generarFolio('LEV', levantamientos), estatus,
      fecha: ahora.toISOString().split('T')[0],
      horaRegistro: ahora.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }),
      ultimaMod: ahora.toISOString().replace('T', ' ').substring(0, 16),
      ultimoModUsuario: 'Carlos V.',
      comercial: 'Carlos V.',
      origen, necesidad, tecnico, sitio, score,
    };
    setLevantamientos(prev => [nuevo, ...prev]);
    localStorage.removeItem('beep-lev-draft');
    resetForm();
    setVista('lista');
  };

  const mapsUrl = (dir) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(dir)}`;
  const navUrl = (dir) => `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(dir)}`;

  const toggleServicio = (sId) => {
    setNecesidad(prev => ({
      ...prev,
      servicios: prev.servicios.includes(sId) ? prev.servicios.filter(s => s !== sId) : [...prev.servicios, sId],
    }));
  };

  // ============================================
  // VISTA DETALLE
  // ============================================
  if (vista === 'detalle' && detalleId) {
    const lev = levantamientos.find(l => l.id === detalleId);
    if (!lev) { setVista('lista'); return null; }
    return (
      <div style={{ fontFamily: PP }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
          <button onClick={() => setVista('lista')} style={{ background: colores.fondo, border: '1px solid #e0e0e0', borderRadius: '8px', padding: '8px 14px', fontSize: '13px', cursor: 'pointer', color: colores.gris }}>← Volver</button>
          <BadgeFolio folio={lev.folio} />
          <EstatusBadge estatus={lev.estatus} />
          <ScoreEstrellas score={lev.score} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: esCelular ? '1fr' : '1fr 1fr', gap: '14px' }}>
          <div style={tarjeta}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: colores.grisClaro, marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Origen</div>
            {[
              { l: 'Tipo', v: lev.origen.tipo },
              { l: 'Cliente', v: lev.origen.esExistente ? lev.origen.clienteNombre : (lev.origen.nombre || '-') },
              { l: 'Empresa', v: lev.origen.esExistente ? lev.origen.clienteNombre : (lev.origen.empresa || '-') },
              { l: 'Fecha', v: lev.fecha },
              { l: 'Comercial', v: lev.comercial },
            ].map((d, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f0f0f0', fontSize: '13px' }}>
                <span style={{ color: colores.grisClaro }}>{d.l}</span>
                <span style={{ color: colores.gris, fontWeight: '500' }}>{d.v}</span>
              </div>
            ))}
          </div>
          <div style={tarjeta}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: colores.grisClaro, marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Necesidad</div>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '10px' }}>
              {(lev.necesidad.servicios || []).map(s => {
                const sv = serviciosDisponibles.find(x => x.id === s);
                return <span key={s} style={{ padding: '4px 10px', borderRadius: '8px', background: '#e6f1fb', color: colores.oscuro, fontSize: '11px', fontWeight: '600' }}>{sv ? sv.icon + ' ' + sv.label : s}</span>;
              })}
            </div>
            <div style={{ fontSize: '13px', color: colores.gris, lineHeight: 1.5, marginBottom: '8px' }}>{lev.necesidad.descripcion}</div>
            <div style={{ fontSize: '12px', color: colores.grisClaro }}>Urgencia: <strong style={{ color: colores.gris }}>{lev.necesidad.urgencia}</strong> · Presupuesto: <strong style={{ color: colores.gris }}>{lev.necesidad.presupuesto}</strong></div>
          </div>
          {lev.sitio?.direccion && (
            <div style={tarjeta}>
              <div style={{ fontSize: '12px', fontWeight: '600', color: colores.grisClaro, marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Sitio</div>
              {[
                { l: 'Dirección', v: lev.sitio.direccion },
                { l: 'Tipo', v: lev.sitio.tipo },
                { l: 'Pisos', v: lev.sitio.pisos },
                { l: 'm²', v: lev.sitio.m2 },
                { l: 'Visita', v: lev.sitio.visita },
              ].map((d, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f0f0f0', fontSize: '13px' }}>
                  <span style={{ color: colores.grisClaro }}>{d.l}</span>
                  <span style={{ color: colores.gris, fontWeight: '500' }}>{d.v || '-'}</span>
                </div>
              ))}
              {lev.sitio.observaciones && <div style={{ fontSize: '13px', color: colores.gris, marginTop: '10px', lineHeight: 1.5 }}>{lev.sitio.observaciones}</div>}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ============================================
  // VISTA NUEVO — WIZARD DE 5 PASOS
  // ============================================
  if (vista === 'nuevo') {
    const score = calcScore({ origen, necesidad, tecnico, sitio });

    return (
      <div style={{ fontFamily: PP, maxWidth: '700px', margin: '0 auto' }}>
        {/* Barra de progreso */}
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '24px', overflowX: 'auto', gap: '4px', padding: '4px 0' }}>
          {pasos.map((p, i) => {
            const completado = p.id < paso;
            const actual = p.id === paso;
            return (
              <React.Fragment key={p.id}>
                <div onClick={() => setPaso(p.id)} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', minWidth: '60px', flexShrink: 0 }}>
                  <div style={{
                    width: '36px', height: '36px', borderRadius: '50%',
                    background: completado ? '#1D9E75' : actual ? colores.oscuro : '#e0e0e0',
                    color: completado || actual ? '#fff' : colores.grisClaro,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '16px', fontWeight: '700', transition: 'all 0.2s',
                  }}>
                    {completado ? '✓' : p.icon}
                  </div>
                  <span style={{ fontSize: '10px', marginTop: '4px', color: actual ? colores.oscuro : colores.grisClaro, fontWeight: actual ? '700' : '400' }}>{p.label}</span>
                </div>
                {i < pasos.length - 1 && <div style={{ flex: 1, height: '2px', background: completado ? '#1D9E75' : '#e0e0e0', minWidth: '20px', marginBottom: '18px' }} />}
              </React.Fragment>
            );
          })}
        </div>

        {/* Draft badge */}
        {localStorage.getItem('beep-lev-draft') && (
          <div style={{ background: '#fffbe6', borderRadius: '8px', padding: '8px 14px', fontSize: '11px', color: '#7a6e00', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            {'💾'} Borrador guardado automáticamente
          </div>
        )}

        {/* PASO 1 — ORIGEN */}
        {paso === 1 && (
          <div style={tarjeta}>
            <div style={{ fontSize: '16px', fontWeight: '700', color: colores.gris, marginBottom: '16px' }}>{'📞'} Origen del contacto</div>
            <div style={{ marginBottom: '14px' }}>
              <label style={labelStyle}>Tipo de contacto</label>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {['Llamada entrante', 'Visita en oficina', 'Visita en sitio', 'Referido', 'WhatsApp', 'Correo'].map(t => (
                  <button key={t} onClick={() => setOrigen({ ...origen, tipo: t })} style={{ padding: '8px 14px', borderRadius: '10px', border: 'none', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: PP, background: origen.tipo === t ? colores.oscuro : '#fff', color: origen.tipo === t ? '#fff' : colores.gris, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>{t}</button>
                ))}
              </div>
            </div>
            <div style={{ marginBottom: '14px' }}>
              <label style={labelStyle}>¿Es cliente existente?</label>
              <div style={{ display: 'flex', gap: '10px' }}>
                {[true, false].map(v => (
                  <button key={String(v)} onClick={() => setOrigen({ ...origen, esExistente: v })} style={{ flex: 1, padding: '12px', borderRadius: '10px', border: 'none', fontSize: '14px', fontWeight: '600', cursor: 'pointer', fontFamily: PP, background: origen.esExistente === v ? colores.oscuro : '#fff', color: origen.esExistente === v ? '#fff' : colores.gris, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>{v ? 'Sí, existente' : 'No, es nuevo'}</button>
                ))}
              </div>
            </div>
            {origen.esExistente ? (
              <div style={{ marginBottom: '14px' }}>
                <label style={labelStyle}>Buscar cliente</label>
                <input value={origen.clienteNombre} onChange={e => setOrigen({ ...origen, clienteNombre: e.target.value })} placeholder="Nombre del cliente o empresa..." style={inputStyle} />
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: esCelular ? '1fr' : '1fr 1fr', gap: '12px' }}>
                <div><label style={labelStyle}>Nombre</label><input value={origen.nombre} onChange={e => setOrigen({ ...origen, nombre: e.target.value })} style={inputStyle} /></div>
                <div><label style={labelStyle}>Empresa</label><input value={origen.empresa} onChange={e => setOrigen({ ...origen, empresa: e.target.value })} style={inputStyle} /></div>
                <div><label style={labelStyle}>Teléfono</label><input value={origen.telefono} onChange={e => setOrigen({ ...origen, telefono: e.target.value })} type="tel" style={inputStyle} /></div>
                <div><label style={labelStyle}>Correo</label><input value={origen.correo} onChange={e => setOrigen({ ...origen, correo: e.target.value })} type="email" style={inputStyle} /></div>
              </div>
            )}
          </div>
        )}

        {/* PASO 2 — NECESIDAD */}
        {paso === 2 && (
          <div style={tarjeta}>
            <div style={{ fontSize: '16px', fontWeight: '700', color: colores.gris, marginBottom: '16px' }}>{'🎯'} Necesidad del cliente</div>
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>Servicios de interés</label>
              <div style={{ display: 'grid', gridTemplateColumns: esCelular ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)', gap: '10px' }}>
                {serviciosDisponibles.map(s => {
                  const sel = necesidad.servicios.includes(s.id);
                  return (
                    <div key={s.id} onClick={() => toggleServicio(s.id)} style={{ padding: '14px 12px', borderRadius: '12px', cursor: 'pointer', textAlign: 'center', background: sel ? `${colores.oscuro}12` : '#fff', border: sel ? `2px solid ${colores.oscuro}` : '2px solid #e0e0e0', transition: 'all 0.15s' }}>
                      <div style={{ fontSize: '24px', marginBottom: '6px' }}>{s.icon}</div>
                      <div style={{ fontSize: '12px', fontWeight: sel ? '700' : '500', color: sel ? colores.oscuro : colores.gris }}>{s.label}</div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div style={{ marginBottom: '14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                <label style={{ ...labelStyle, marginBottom: 0 }}>Descripción de la necesidad</label>
                <span title="El asistente de IA estará disponible próximamente" style={{ padding: '4px 10px', borderRadius: '8px', border: '1px solid #e0e0e0', background: '#f3f4f4', color: '#5e666e', fontSize: '11px', fontWeight: '600', cursor: 'not-allowed', fontFamily: PP, opacity: 0.7 }}>{'🤖'} Asistente IA — Próximamente</span>
              </div>
              <textarea value={necesidad.descripcion} onChange={e => setNecesidad({ ...necesidad, descripcion: e.target.value })} rows={4} placeholder="Describe lo que el cliente necesita..." style={{ ...inputStyle, resize: 'vertical' }} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={labelStyle}>Urgencia</label>
                <select value={necesidad.urgencia} onChange={e => setNecesidad({ ...necesidad, urgencia: e.target.value })} style={inputStyle}>
                  {['Inmediata', 'Esta semana', 'Este mes', 'Sin definir'].map(u => <option key={u}>{u}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Presupuesto</label>
                <select value={necesidad.presupuesto} onChange={e => setNecesidad({ ...necesidad, presupuesto: e.target.value })} style={inputStyle}>
                  {['Menos de $10k', '$10k-$50k', '$50k-$150k', '$150k-$500k', 'Más de $500k', 'No definido'].map(p => <option key={p}>{p}</option>)}
                </select>
              </div>
            </div>
          </div>
        )}

        {/* PASO 3 — PREGUNTAS TÉCNICAS */}
        {paso === 3 && (
          <div style={tarjeta}>
            <div style={{ fontSize: '16px', fontWeight: '700', color: colores.gris, marginBottom: '16px' }}>{'🔧'} Preguntas técnicas</div>
            {necesidad.servicios.length === 0 && (
              <div style={{ padding: '20px', textAlign: 'center', color: colores.grisClaro, fontSize: '13px' }}>Selecciona al menos un servicio en el paso anterior para ver las preguntas técnicas.</div>
            )}
            {necesidad.servicios.includes('CCTV') && (
              <div style={{ marginBottom: '20px', padding: '16px', background: '#fafafa', borderRadius: '12px' }}>
                <div style={{ fontSize: '14px', fontWeight: '600', color: colores.gris, marginBottom: '12px' }}>{'📹'} CCTV</div>
                <div style={{ display: 'grid', gridTemplateColumns: esCelular ? '1fr' : '1fr 1fr', gap: '10px' }}>
                  <div><label style={labelStyle}>¿Cuántas cámaras?</label><input value={tecnico.cctv.camaras} onChange={e => setTecnico({ ...tecnico, cctv: { ...tecnico.cctv, camaras: e.target.value } })} style={inputStyle} /></div>
                  <div><label style={labelStyle}>Instalación</label><select value={tecnico.cctv.instalacion} onChange={e => setTecnico({ ...tecnico, cctv: { ...tecnico.cctv, instalacion: e.target.value } })} style={inputStyle}><option value="">Seleccionar</option><option>Interior</option><option>Exterior</option><option>Ambas</option></select></div>
                  <div><label style={labelStyle}>¿Tiene cableado?</label><select value={tecnico.cctv.cableado} onChange={e => setTecnico({ ...tecnico, cctv: { ...tecnico.cctv, cableado: e.target.value } })} style={inputStyle}><option value="">Seleccionar</option><option>Sí</option><option>No</option><option>No sé</option></select></div>
                  <div><label style={labelStyle}>¿Tiene DVR/NVR?</label><select value={tecnico.cctv.dvr} onChange={e => setTecnico({ ...tecnico, cctv: { ...tecnico.cctv, dvr: e.target.value } })} style={inputStyle}><option value="">Seleccionar</option><option>Sí</option><option>No</option></select></div>
                  {tecnico.cctv.dvr === 'Sí' && <div><label style={labelStyle}>Marca y canales</label><input value={tecnico.cctv.marcaDvr} onChange={e => setTecnico({ ...tecnico, cctv: { ...tecnico.cctv, marcaDvr: e.target.value } })} placeholder="Ej: Hikvision 16CH" style={inputStyle} /></div>}
                  <div><label style={labelStyle}>Resolución</label><select value={tecnico.cctv.resolucion} onChange={e => setTecnico({ ...tecnico, cctv: { ...tecnico.cctv, resolucion: e.target.value } })} style={inputStyle}><option value="">Seleccionar</option><option>HD</option><option>Full HD</option><option>4K</option><option>No sé</option></select></div>
                  <div><label style={labelStyle}>¿Acceso remoto?</label><select value={tecnico.cctv.remoto} onChange={e => setTecnico({ ...tecnico, cctv: { ...tecnico.cctv, remoto: e.target.value } })} style={inputStyle}><option value="">Seleccionar</option><option>Sí</option><option>No</option></select></div>
                </div>
                <div style={{ marginTop: '10px' }}><label style={labelStyle}>Zonas difíciles</label><textarea value={tecnico.cctv.zonasDificiles} onChange={e => setTecnico({ ...tecnico, cctv: { ...tecnico.cctv, zonasDificiles: e.target.value } })} rows={2} placeholder="¿Hay zonas sin señal o difícil acceso?" style={{ ...inputStyle, resize: 'vertical' }} /></div>
              </div>
            )}
            {necesidad.servicios.includes('acceso') && (
              <div style={{ marginBottom: '20px', padding: '16px', background: '#fafafa', borderRadius: '12px' }}>
                <div style={{ fontSize: '14px', fontWeight: '600', color: colores.gris, marginBottom: '12px' }}>{'🔐'} Control de acceso</div>
                <div style={{ display: 'grid', gridTemplateColumns: esCelular ? '1fr' : '1fr 1fr', gap: '10px' }}>
                  <div><label style={labelStyle}>¿Cuántos puntos de acceso?</label><input value={tecnico.acceso.puntos} onChange={e => setTecnico({ ...tecnico, acceso: { ...tecnico.acceso, puntos: e.target.value } })} style={inputStyle} /></div>
                  <div><label style={labelStyle}>Tipo</label><select value={tecnico.acceso.tipo} onChange={e => setTecnico({ ...tecnico, acceso: { ...tecnico.acceso, tipo: e.target.value } })} style={inputStyle}><option value="">Seleccionar</option><option>Tarjeta</option><option>Huella</option><option>Facial</option><option>Combinado</option></select></div>
                  <div><label style={labelStyle}>¿Integrar con CCTV?</label><select value={tecnico.acceso.integracionCctv} onChange={e => setTecnico({ ...tecnico, acceso: { ...tecnico.acceso, integracionCctv: e.target.value } })} style={inputStyle}><option value="">Seleccionar</option><option>Sí</option><option>No</option></select></div>
                  <div><label style={labelStyle}>¿Horarios por persona?</label><select value={tecnico.acceso.horarios} onChange={e => setTecnico({ ...tecnico, acceso: { ...tecnico.acceso, horarios: e.target.value } })} style={inputStyle}><option value="">Seleccionar</option><option>Sí</option><option>No</option></select></div>
                </div>
              </div>
            )}
            {necesidad.servicios.includes('redes') && (
              <div style={{ marginBottom: '20px', padding: '16px', background: '#fafafa', borderRadius: '12px' }}>
                <div style={{ fontSize: '14px', fontWeight: '600', color: colores.gris, marginBottom: '12px' }}>{'🌐'} Redes</div>
                <div style={{ display: 'grid', gridTemplateColumns: esCelular ? '1fr' : '1fr 1fr', gap: '10px' }}>
                  <div><label style={labelStyle}>Puntos de red</label><input value={tecnico.redes.puntos} onChange={e => setTecnico({ ...tecnico, redes: { ...tecnico.redes, puntos: e.target.value } })} style={inputStyle} /></div>
                  <div><label style={labelStyle}>Área (m²)</label><input value={tecnico.redes.area} onChange={e => setTecnico({ ...tecnico, redes: { ...tecnico.redes, area: e.target.value } })} style={inputStyle} /></div>
                  <div><label style={labelStyle}>¿Tiene rack?</label><select value={tecnico.redes.rack} onChange={e => setTecnico({ ...tecnico, redes: { ...tecnico.redes, rack: e.target.value } })} style={inputStyle}><option value="">Seleccionar</option><option>Sí</option><option>No</option></select></div>
                  <div><label style={labelStyle}>¿Requiere WiFi?</label><select value={tecnico.redes.wifi} onChange={e => setTecnico({ ...tecnico, redes: { ...tecnico.redes, wifi: e.target.value } })} style={inputStyle}><option value="">Seleccionar</option><option>Sí</option><option>No</option></select></div>
                </div>
              </div>
            )}
            {necesidad.servicios.includes('mantenimiento') && (
              <div style={{ marginBottom: '20px', padding: '16px', background: '#fafafa', borderRadius: '12px' }}>
                <div style={{ fontSize: '14px', fontWeight: '600', color: colores.gris, marginBottom: '12px' }}>{'🔧'} Mantenimiento</div>
                <div style={{ display: 'grid', gridTemplateColumns: esCelular ? '1fr' : '1fr 1fr', gap: '10px' }}>
                  <div><label style={labelStyle}>Sistemas instalados</label><input value={tecnico.mantenimiento.sistemas} onChange={e => setTecnico({ ...tecnico, mantenimiento: { ...tecnico.mantenimiento, sistemas: e.target.value } })} placeholder="CCTV, alarmas, etc." style={inputStyle} /></div>
                  <div><label style={labelStyle}>¿Quién lo instaló?</label><select value={tecnico.mantenimiento.instalador} onChange={e => setTecnico({ ...tecnico, mantenimiento: { ...tecnico.mantenimiento, instalador: e.target.value } })} style={inputStyle}><option value="">Seleccionar</option><option>Beep</option><option>Otra empresa</option><option>No sabe</option></select></div>
                  <div><label style={labelStyle}>¿Contrato activo?</label><select value={tecnico.mantenimiento.contrato} onChange={e => setTecnico({ ...tecnico, mantenimiento: { ...tecnico.mantenimiento, contrato: e.target.value } })} style={inputStyle}><option value="">Seleccionar</option><option>Sí</option><option>No</option></select></div>
                  <div><label style={labelStyle}>Frecuencia deseada</label><select value={tecnico.mantenimiento.frecuencia} onChange={e => setTecnico({ ...tecnico, mantenimiento: { ...tecnico.mantenimiento, frecuencia: e.target.value } })} style={inputStyle}><option value="">Seleccionar</option><option>Mensual</option><option>Trimestral</option><option>Semestral</option><option>Anual</option></select></div>
                </div>
                <div style={{ marginTop: '10px' }}><label style={labelStyle}>Problema actual</label><textarea value={tecnico.mantenimiento.problema} onChange={e => setTecnico({ ...tecnico, mantenimiento: { ...tecnico.mantenimiento, problema: e.target.value } })} rows={2} style={{ ...inputStyle, resize: 'vertical' }} /></div>
              </div>
            )}
            {necesidad.servicios.includes('alarmas') && (
              <div style={{ marginBottom: '20px', padding: '16px', background: '#fafafa', borderRadius: '12px' }}>
                <div style={{ fontSize: '14px', fontWeight: '600', color: colores.gris, marginBottom: '12px' }}>{'🔔'} Alarmas</div>
                <div style={{ display: 'grid', gridTemplateColumns: esCelular ? '1fr' : '1fr 1fr', gap: '10px' }}>
                  <div><label style={labelStyle}>Zonas / sensores</label><input value={tecnico.alarmas.zonas} onChange={e => setTecnico({ ...tecnico, alarmas: { ...tecnico.alarmas, zonas: e.target.value } })} style={inputStyle} /></div>
                  <div><label style={labelStyle}>Tipo</label><select value={tecnico.alarmas.tipo} onChange={e => setTecnico({ ...tecnico, alarmas: { ...tecnico.alarmas, tipo: e.target.value } })} style={inputStyle}><option value="">Seleccionar</option><option>Residencial</option><option>Comercial</option><option>Industrial</option></select></div>
                  <div><label style={labelStyle}>¿Monitoreo 24/7?</label><select value={tecnico.alarmas.monitoreo} onChange={e => setTecnico({ ...tecnico, alarmas: { ...tecnico.alarmas, monitoreo: e.target.value } })} style={inputStyle}><option value="">Seleccionar</option><option>Sí</option><option>No</option></select></div>
                  <div><label style={labelStyle}>¿Línea tel / internet?</label><select value={tecnico.alarmas.lineaTel} onChange={e => setTecnico({ ...tecnico, alarmas: { ...tecnico.alarmas, lineaTel: e.target.value } })} style={inputStyle}><option value="">Seleccionar</option><option>Sí</option><option>No</option></select></div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* PASO 4 — SITIO */}
        {paso === 4 && (
          <div style={tarjeta}>
            <div style={{ fontSize: '16px', fontWeight: '700', color: colores.gris, marginBottom: '16px' }}>{'📍'} Datos del sitio</div>
            <div style={{ display: 'grid', gridTemplateColumns: esCelular ? '1fr' : '1fr 1fr', gap: '12px' }}>
              <div style={{ gridColumn: esCelular ? '1' : '1 / -1' }}>
                <label style={labelStyle}>Dirección</label>
                <input value={sitio.direccion} onChange={e => setSitio({ ...sitio, direccion: e.target.value })} placeholder="Calle, número, colonia, ciudad, estado" style={inputStyle} />
                {sitio.direccion && (
                  <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                    <a href={mapsUrl(sitio.direccion)} target="_blank" rel="noopener noreferrer" style={{ padding: '6px 12px', borderRadius: '8px', background: '#e6f1fb', color: colores.oscuro, fontSize: '12px', fontWeight: '600', textDecoration: 'none', fontFamily: PP, display: 'flex', alignItems: 'center', gap: '4px' }}>{'📍'} Ver en mapa</a>
                    <a href={navUrl(sitio.direccion)} target="_blank" rel="noopener noreferrer" style={{ padding: '6px 12px', borderRadius: '8px', background: '#e8faf5', color: '#1D9E75', fontSize: '12px', fontWeight: '600', textDecoration: 'none', fontFamily: PP, display: 'flex', alignItems: 'center', gap: '4px' }}>{'🚗'} Cómo llegar</a>
                  </div>
                )}
              </div>
              <div><label style={labelStyle}>Tipo de inmueble</label><select value={sitio.tipo} onChange={e => setSitio({ ...sitio, tipo: e.target.value })} style={inputStyle}><option>Oficina</option><option>Bodega</option><option>Local comercial</option><option>Residencia</option><option>Industrial</option><option>Otro</option></select></div>
              <div><label style={labelStyle}>Pisos</label><input value={sitio.pisos} onChange={e => setSitio({ ...sitio, pisos: e.target.value })} type="number" style={inputStyle} /></div>
              <div><label style={labelStyle}>Metros cuadrados</label><input value={sitio.m2} onChange={e => setSitio({ ...sitio, m2: e.target.value })} type="number" style={inputStyle} /></div>
              <div><label style={labelStyle}>¿Se puede visitar?</label><select value={sitio.visita} onChange={e => setSitio({ ...sitio, visita: e.target.value })} style={inputStyle}><option>Sí</option><option>No</option><option>Ya se hizo</option></select></div>
              {sitio.visita === 'Sí' && <div><label style={labelStyle}>Fecha propuesta</label><input value={sitio.fechaVisita} onChange={e => setSitio({ ...sitio, fechaVisita: e.target.value })} type="date" style={inputStyle} /></div>}
            </div>
            <div style={{ marginTop: '14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                <label style={{ ...labelStyle, marginBottom: 0 }}>Observaciones</label>
                <span title="El asistente de IA estará disponible próximamente" style={{ padding: '4px 10px', borderRadius: '8px', border: '1px solid #e0e0e0', background: '#f3f4f4', color: '#5e666e', fontSize: '11px', fontWeight: '600', cursor: 'not-allowed', fontFamily: PP, opacity: 0.7 }}>{'🤖'} Asistente IA — Próximamente</span>
              </div>
              <textarea value={sitio.observaciones} onChange={e => setSitio({ ...sitio, observaciones: e.target.value })} rows={3} placeholder="Observaciones del sitio, accesos, particularidades..." style={{ ...inputStyle, resize: 'vertical' }} />
            </div>
          </div>
        )}

        {/* PASO 5 — RESUMEN */}
        {paso === 5 && (
          <div>
            <div style={{ ...tarjeta, textAlign: 'center', borderLeft: `4px solid ${score >= 4 ? '#1D9E75' : score >= 3 ? '#f4a261' : colores.rojo}` }}>
              <div style={{ fontSize: '16px', fontWeight: '700', color: colores.gris, marginBottom: '8px' }}>Calificación del prospecto</div>
              <ScoreEstrellas score={score} />
              <div style={{ fontSize: '13px', color: colores.grisClaro, marginTop: '8px', lineHeight: 1.5 }}>
                {score >= 5 ? 'Prospecto listo para cotizar — genera cotización ahora' : score >= 3 ? 'Agenda visita de levantamiento para completar información' : 'Prospecto en etapa inicial — programa seguimiento en 3 días'}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: esCelular ? '1fr' : '1fr 1fr', gap: '14px', marginBottom: '20px' }}>
              <div style={tarjeta}>
                <div style={{ fontSize: '12px', fontWeight: '600', color: colores.grisClaro, marginBottom: '8px', textTransform: 'uppercase' }}>Origen</div>
                <div style={{ fontSize: '13px', color: colores.gris }}>{origen.tipo}</div>
                <div style={{ fontSize: '13px', color: colores.gris }}>{origen.esExistente ? origen.clienteNombre : (origen.nombre + ' — ' + origen.empresa)}</div>
              </div>
              <div style={tarjeta}>
                <div style={{ fontSize: '12px', fontWeight: '600', color: colores.grisClaro, marginBottom: '8px', textTransform: 'uppercase' }}>Necesidad</div>
                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '4px' }}>
                  {necesidad.servicios.map(s => <span key={s} style={{ padding: '2px 8px', borderRadius: '6px', background: '#e6f1fb', color: colores.oscuro, fontSize: '10px', fontWeight: '600' }}>{s}</span>)}
                </div>
                <div style={{ fontSize: '12px', color: colores.gris }}>{necesidad.urgencia} · {necesidad.presupuesto}</div>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button onClick={() => guardarLevantamiento('Completado')} style={{ width: '100%', padding: '14px', background: colores.oscuro, color: '#fff', border: 'none', borderRadius: '12px', fontSize: '15px', fontWeight: '700', cursor: 'pointer', fontFamily: PP }}>Guardar como prospecto</button>
              <button onClick={() => guardarLevantamiento('Completado')} style={{ width: '100%', padding: '14px', background: '#1D9E75', color: '#fff', border: 'none', borderRadius: '12px', fontSize: '15px', fontWeight: '700', cursor: 'pointer', fontFamily: PP }}>Guardar y cotizar</button>
              <button onClick={() => guardarLevantamiento('Completado')} style={{ width: '100%', padding: '14px', background: '#f4a261', color: '#fff', border: 'none', borderRadius: '12px', fontSize: '15px', fontWeight: '700', cursor: 'pointer', fontFamily: PP }}>Guardar y agendar visita</button>
            </div>
          </div>
        )}

        {/* Navegación entre pasos */}
        <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
          {paso > 1 && <button onClick={() => setPaso(paso - 1)} style={{ flex: 1, padding: '14px', background: '#fff', color: colores.gris, border: '1px solid #e0e0e0', borderRadius: '12px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', fontFamily: PP }}>← Anterior</button>}
          {paso < 5 && <button onClick={() => setPaso(paso + 1)} style={{ flex: 1, padding: '14px', background: colores.oscuro, color: '#fff', border: 'none', borderRadius: '12px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', fontFamily: PP }}>Siguiente →</button>}
          <button onClick={() => { guardarLevantamiento('Borrador'); }} style={{ padding: '14px 20px', background: colores.fondo, color: colores.grisClaro, border: '1px solid #e0e0e0', borderRadius: '12px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: PP }}>Guardar borrador</button>
        </div>
      </div>
    );
  }

  // ============================================
  // VISTA LISTA — Historial de levantamientos
  // ============================================
  const filtrados = levantamientos.filter(l => {
    if (filtroEstatus && l.estatus !== filtroEstatus) return false;
    if (busqueda) {
      const q = busqueda.toLowerCase();
      const nombre = l.origen.esExistente ? l.origen.clienteNombre : (l.origen.nombre || '');
      const empresa = l.origen.esExistente ? l.origen.clienteNombre : (l.origen.empresa || '');
      if (!(l.folio || '').toLowerCase().includes(q) && !nombre.toLowerCase().includes(q) && !empresa.toLowerCase().includes(q)) return false;
    }
    if (filtroFecha) {
      const hoy = new Date(); hoy.setHours(0,0,0,0);
      const fLev = new Date(l.fecha);
      if (filtroFecha === 'hoy' && fLev.toDateString() !== hoy.toDateString()) return false;
      if (filtroFecha === 'semana') { const fin = new Date(hoy); fin.setDate(hoy.getDate() - 7); if (fLev < fin) return false; }
      if (filtroFecha === 'mes') { if (fLev.getMonth() !== hoy.getMonth() || fLev.getFullYear() !== hoy.getFullYear()) return false; }
    }
    return true;
  });

  return (
    <div style={{ fontFamily: PP }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
        <ContadorResultados total={levantamientos.length} filtrados={filtrados.length} />
        <button onClick={iniciarNuevo} style={{ padding: '10px 20px', background: colores.oscuro, color: '#fff', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', fontFamily: PP }}>+ Nuevo levantamiento</button>
      </div>

      <BarraBusqueda value={busqueda} onChange={setBusqueda} placeholder="Buscar por folio, cliente o empresa..." />

      <FiltrosRapidos
        opciones={[
          { value: '', label: 'Todos', count: levantamientos.length },
          { value: 'Borrador', label: 'Borradores', count: levantamientos.filter(l => l.estatus === 'Borrador').length },
          { value: 'Completado', label: 'Completados', count: levantamientos.filter(l => l.estatus === 'Completado').length },
          { value: 'Convertido', label: 'Convertidos', count: levantamientos.filter(l => l.estatus === 'Convertido').length },
        ]}
        activo={filtroEstatus}
        onChange={setFiltroEstatus}
      />

      {/* Filtros de fecha */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', flexWrap: 'wrap' }}>
        {[
          { value: '', label: 'Todas las fechas' },
          { value: 'hoy', label: 'Hoy' },
          { value: 'semana', label: 'Esta semana' },
          { value: 'mes', label: 'Este mes' },
        ].map(f => (
          <button key={f.value} onClick={() => setFiltroFecha(f.value)} style={{ padding: '4px 12px', borderRadius: '16px', border: 'none', fontSize: '11px', fontWeight: '600', cursor: 'pointer', fontFamily: PP, background: filtroFecha === f.value ? colores.principal : '#fff', color: filtroFecha === f.value ? '#fff' : colores.grisClaro, boxShadow: '0 1px 2px rgba(0,0,0,0.06)' }}>{f.label}</button>
        ))}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {filtrados.map(lev => {
          const nombre = lev.origen.esExistente ? lev.origen.clienteNombre : (lev.origen.nombre || lev.origen.empresa || 'Sin nombre');
          return (
            <div key={lev.id} onClick={() => { setDetalleId(lev.id); setVista('detalle'); }} style={{ background: '#fff', borderRadius: '14px', padding: '16px', cursor: 'pointer', borderLeft: `4px solid ${lev.score >= 4 ? '#1D9E75' : lev.score >= 3 ? '#f4a261' : colores.rojo}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', flexWrap: 'wrap' }}>
                <BadgeFolio folio={lev.folio} />
                <EstatusBadge estatus={lev.estatus} />
                <ScoreEstrellas score={lev.score} />
                <span style={{ fontSize: '11px', color: colores.grisClaro, marginLeft: 'auto' }}>{lev.fecha} {lev.horaRegistro || ''}</span>
              </div>
              <div style={{ fontSize: '14px', fontWeight: '600', color: colores.gris, marginBottom: '4px' }}>{nombre}</div>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '4px' }}>
                {(lev.necesidad.servicios || []).map(s => {
                  const sv = serviciosDisponibles.find(x => x.id === s);
                  return <span key={s} style={{ padding: '2px 8px', borderRadius: '6px', background: '#e6f1fb', color: colores.oscuro, fontSize: '10px', fontWeight: '600' }}>{sv ? sv.icon + ' ' + sv.label : s}</span>;
                })}
                <span style={{ fontSize: '11px', color: colores.grisClaro }}>{lev.necesidad.urgencia} · {lev.necesidad.presupuesto}</span>
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                {lev.sitio?.direccion && (
                  <a href={mapsUrl(lev.sitio.direccion)} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{ fontSize: '11px', color: colores.principal, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '3px' }}>{'📍'} {lev.sitio.direccion.substring(0, 40)}{lev.sitio.direccion.length > 40 ? '...' : ''}</a>
                )}
                {lev.ultimaMod && (
                  <span style={{ fontSize: '10px', color: colores.grisClaro, marginLeft: 'auto' }}>Mod: {lev.ultimaMod} por {lev.ultimoModUsuario}</span>
                )}
              </div>
            </div>
          );
        })}
        {filtrados.length === 0 && (
          <div style={{ background: '#fff', borderRadius: '14px', padding: '32px', textAlign: 'center', fontSize: '13px', color: colores.grisClaro }}>No hay levantamientos con ese filtro</div>
        )}
      </div>
    </div>
  );
}

export default Levantamiento;
